import GlazeClient from "@framebridge/toolbox/apihelpers/glazeClient";
import { ref } from "vue";

// eslint-disable-next-line no-undef
const baseUrl = SDG.Data.joineryHost || "https://staging.framebridge.com";
const routeUrl = "api/v1/delivery_estimates/generate";
const optionsRouteUrl = "api/v1/delivery_estimates/options";

const zipcode = ref(null);
const estimatedDate = ref(null);
const currentReceiveBy = ref(null);

// Listen for zipcode being set by store-locator (ipstack geolocation)
window.addEventListener("SDG-zip-updated", (event) => {
  const { zip } = event.detail || {};
  if (zip) {
    zipcode.value = zip;
  }
});

function useDelivery() {
  const customerPackaging = ref(false);
  const expedited = ref(false);
  const fixedSizeBundle = ref(false);
  const postPurchaseDesignConsultation = ref(false);
  const quickShip = ref(false);

  if (zipcode.value === null) {
    const zip = sessionStorage.getItem("SDG-zip");
    if (zip) {
      zipcode.value = zip;
    }
  }

  function adjustConveyance(conveyance) {
    if (["upload", "uploaded"].includes(conveyance)) {
      return "upload";
    }
    return "mailin";
  }

  function updateZipcode(zip) {
    zipcode.value = zip;
    sessionStorage.setItem("SDG-zip", zip);

    return zipcode.value;
  }

  async function fetchDeliveryEstimate(conveyance = "upload", val = null) {
    const zipcodeValue = val || zipcode.value;

    if (!zipcodeValue) {
      console.error("Zipcode is required for delivery estimate");
      return null;
    }

    const requestData = {
      conveyance: adjustConveyance(conveyance),
      quick_ship: conveyance === "mailin" ? false : quickShip.value,
      expedited_shipping_days: quickShip.value ? 1 : null,
      customer_packaging: customerPackaging.value,
      fixed_size_bundle: fixedSizeBundle.value,
      post_purchase_design_consultation: postPurchaseDesignConsultation.value,
      expedited: conveyance === "mailin" ? false : expedited.value,
      zipcode: zipcodeValue,
      test_date: ""
    };


    const glazeClient = await new GlazeClient(
      null,
      {
        rejectIfNotOk: true,
        baseUrl
      }
    )
      .post(routeUrl, requestData);

    if (glazeClient && glazeClient?.data) {
      estimatedDate.value = glazeClient.data;
      const { deliveryEstimate } = glazeClient.data;

      if (deliveryEstimate) {
        const { getItBy } = deliveryEstimate;

        if (getItBy) {
          currentReceiveBy.value = getItBy;
        }
      }

      if (val) {
        updateZipcode(val);
      }

      return {
        estimatedDate: estimatedDate.value
      };
    }

    return null;
  }

  async function fetchExpeditedDeliveryEstimate(conveyance = "upload", val = null, frameSpecNumber = null) {
    const zipcodeValue = val || zipcode.value;

    if (!zipcodeValue) {
      console.error("Zipcode is required for delivery estimate");
      return null;
    }

    const requestData = {
      conveyance: adjustConveyance(conveyance),
      quick_ship: conveyance === "mailin" ? false : quickShip.value,
      expedited_shipping_days: 1,
      customer_packaging: customerPackaging.value,
      fixed_size_bundle: fixedSizeBundle.value,
      post_purchase_design_consultation: postPurchaseDesignConsultation.value,
      expedited: true,
      zipcode: zipcodeValue,
      test_date: "",
      ...(frameSpecNumber && { frame_spec_number: frameSpecNumber })
    };

    try {
      const glazeClient = await new GlazeClient(
        null,
        {
          rejectIfNotOk: true,
          baseUrl
        }
      )
        .post(routeUrl, requestData);

      if (glazeClient && glazeClient?.data) {
        return glazeClient.data;
      }

      return null;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  async function fetchSingleDeliveryEstimate(val = null, conveyance = "upload", frameSpecNumber = null) {
    try {
      const requestData = {
        quick_ship: quickShip.value,
        customer_packaging: customerPackaging.value,
        expedited: expedited.value,
        fixed_size_bundle: fixedSizeBundle.value,
        post_purchase_design_consultation: postPurchaseDesignConsultation.value,
        conveyance: adjustConveyance(conveyance),
        zipcode: val || zipcode.value,
        test_date: "",
        ...(frameSpecNumber && { frame_spec_number: frameSpecNumber })
      };
      const glazeClient = await new GlazeClient(
        null,
        {
          rejectIfNotOk: true,
          baseUrl
        }
      )
        .post(routeUrl, requestData);

      if (glazeClient && glazeClient?.data) {
        const { deliveryEstimate } = glazeClient.data;

        if (deliveryEstimate) {
          const {
            estimatedDeliveryDate,
            extendedDelayTimeframe,
            getItBy,
            shipArtByDate
          } = deliveryEstimate;

          const deliveryDate = estimatedDeliveryDate;

          return {
            deliveryDate,
            message: extendedDelayTimeframe,
            shipArtByDate,
            dates: getItBy
          };
        }
        return null;
      }

      return null;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  /**
   * Fetches all delivery estimate options in a single API call.
   * Returns estimates for physical/digital with holiday, evergreen, expedited, and overnight options.
   *
   * @param {Object} options
   * @param {string} options.zipcode - Shipping ZIP code
   * @param {boolean} options.customerPackaging - True for ship to us (customer packaging)
   * @param {boolean} options.dropOffInStore - True for store drop-off
   * @param {string} options.dropOffInStoreId - Store ID for drop-off, can use id or name
   * @param {string} options.dropOffInStoreName - Store name for drop-off, can use name or id
   * @param {string} options.frameSpecNumber - FrameSpec number
   * @param {boolean} options.postPurchaseDesignConsultation - True for designers choice
   * @param {boolean} options.quickShip - True for quick ship products (tag)
   * @param {boolean} options.readymade - True for ready-made products (tag)
   * @returns {Object|null} deliveryEstimateOptions with physical/digital estimate scenarios
   */
  async function fetchDeliveryEstimateOptions({
    zipcode: zip = null,
    customerPackaging = null,
    dropOffInStore = null,
    dropOffInStoreId = null,
    dropOffInStoreName = null,
    frameSpecNumber = null,
    postPurchaseDesignConsultation = null,
    quickShip = null,
    readymade = null
  } = {}) {
    const zipcodeValue = zip || zipcode.value;

    if (!zipcodeValue) {
      console.error("❌ [Delivery Estimator] Zipcode is required for delivery estimate options");
      return null;
    }

    const requestData = {
      zipcode: zipcodeValue,
      customer_packaging: customerPackaging,
      drop_off_in_store: dropOffInStore,
      drop_off_in_store_id: dropOffInStoreId,
      drop_off_in_store_name: dropOffInStoreName,
      frame_spec_number: frameSpecNumber,
      post_purchase_design_consultation: postPurchaseDesignConsultation,
      quick_ship: quickShip,
      readymade
    };

    try {
      const glazeClient = await new GlazeClient(
        null,
        {
          rejectIfNotOk: true,
          baseUrl
        }
      )
        .post(optionsRouteUrl, requestData);

      if (glazeClient?.data?.deliveryEstimateOptions) {
        return glazeClient.data.deliveryEstimateOptions;
      }

      return null;
    } catch (err) {
      console.error("Error fetching delivery estimate options:", err);
      return null;
    }
  }

  return {
    currentReceiveBy,
    estimatedDate,
    zipcode,
    quickShip,
    customerPackaging,
    expedited,
    fixedSizeBundle,
    postPurchaseDesignConsultation,
    fetchDeliveryEstimate,
    fetchExpeditedDeliveryEstimate,
    fetchSingleDeliveryEstimate,
    fetchDeliveryEstimateOptions,
    updateZipcode
  };
}

export default useDelivery;
